import { Menu, LogOut, User, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { FreshnessChip } from "@/components/layout/freshness-chip";

interface HeaderProps {
  onToggleSidebar?: () => void;
  basePath?: string;
}

export function Header({ onToggleSidebar, basePath = "/app" }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = user?.email
    ? user.email
        .split("@")[0]
        .split(".")
        .map((p) => p[0]?.toUpperCase())
        .join("")
        .slice(0, 2)
    : "U";

  return (
    <header className="flex h-14 items-center gap-4 border-b bg-background px-4 lg:px-6">
      {/* Mobile menu toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onToggleSidebar}
      >
        <Menu className="h-5 w-5" />
        <span className="sr-only">Toggle menu</span>
      </Button>

      {/* Logo / brand */}
      <div className="flex items-center gap-2">
        <img src="/logo.png" alt="" aria-hidden className="h-7 w-auto" />
        <span className="hidden text-lg font-semibold tracking-tight md:inline-block">
          prayingmantis
        </span>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Fleet freshness hint */}
      <FreshnessChip basePath={basePath} />

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-accent">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
          <span className="hidden text-sm font-medium md:inline-block">
            {user?.email ?? "User"}
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>My Account</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate(`${basePath}/settings`)}>
            <User className="mr-2 h-4 w-4" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout}>
            <LogOut className="mr-2 h-4 w-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
